import { Effect } from "effect";
import { CHAT_ATTACHMENT_LIMITS, CHAT_ATTACHMENT_MIME_TYPES, type ChatAttachmentMime } from "@workerstation/shared/chat-contract";
import type { ChatFileIo } from "./composer-service.ts";
const IMAGE_MIME_BY_EXTENSION = new Map<string, ChatAttachmentMime>([
  ["gif", "image/gif"],
  ["jpeg", "image/jpeg"],
  ["jpg", "image/jpeg"],
  ["png", "image/png"],
  ["webp", "image/webp"]
]);

const TEXT_APPLICATION_MIMES = new Set([
  "application/json",
  "application/ld+json",
  "application/toml",
  "application/x-toml",
  "application/x-yaml",
  "application/xml",
  "application/yaml"
]);

export const attachmentMime = (file: File, io: typeof ChatFileIo.Service): Effect.Effect<ChatAttachmentMime | null> => Effect.gen(function* () {
  const declared = file.type.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (isAttachmentMime(declared) && declared !== "text/plain") {
    return declared;
  }
  if (
    declared.startsWith("text/") ||
    TEXT_APPLICATION_MIMES.has(declared) ||
    declared.endsWith("+json") ||
    declared.endsWith("+xml")
  ) {
    return "text/plain";
  }
  const extension = file.name.includes(".")
    ? file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase()
    : "";
  const fallback = IMAGE_MIME_BY_EXTENSION.get(extension);
  if ((!declared || declared === "application/octet-stream") && fallback) return fallback;
  if ((!declared || declared === "application/octet-stream") && extension === "pdf") {
    return "application/pdf";
  }
    const bytes = yield* io.prefix(file);
    if (bytes === null) return null;
    if (bytes.some((byte) => byte === 0)) return null;
    const controls = bytes.filter(
      (byte) => byte < 9 || (byte > 13 && byte < 32)
    ).length;
    return bytes.length === 0 || controls / bytes.length <= 0.3 ? "text/plain" : null;
});

function isAttachmentMime(value: string): value is ChatAttachmentMime {
  return CHAT_ATTACHMENT_MIME_TYPES.some((mime) => mime === value);
}

export function attachmentFilename(file: File, mime: ChatAttachmentMime): string {
  const name = file.name.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  if (name) return name.slice(0, CHAT_ATTACHMENT_LIMITS.filenameChars);
  const extension: Record<ChatAttachmentMime, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "text/plain": "txt"
  };
  return `attachment.${extension[mime]}`;
}

export function dataUrlBytes(dataUrl: string): number {
  const payload = dataUrl.slice(dataUrl.indexOf(",") + 1);
  if (!payload) return 0;
  return Math.floor((payload.length * 3) / 4) -
    (payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0);
}

export function mib(bytes: number): number {
  return bytes / (1024 * 1024);
}
